import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Compass, ArrowLeft, Home } from 'lucide-react';

/**
 * Branded 404. `variant="inline"` renders inside the dashboard chrome;
 * the default renders as a standalone public page with the night-sky backdrop.
 */
const NotFoundPage = ({ variant = 'page' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const inline = variant === 'inline';

  const body = (
    <div
      className={`w-full max-w-xl ${inline ? 'py-16' : ''}`}
      data-testid="not-found-page"
    >
      <div className="flex items-center gap-3 mb-6">
        <Compass className="w-7 h-7 text-amber-300" />
        <span className="text-sm uppercase tracking-[0.3em] text-amber-300/80">
          Page not found
        </span>
      </div>

      <h1
        className="text-4xl sm:text-5xl lg:text-6xl text-white mb-5"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        This path doesn&apos;t exist yet
      </h1>

      <p className="text-base text-gray-300 mb-2">
        The page you were looking for isn&apos;t here — it may have moved, or the link
        may have been mistyped.
      </p>
      <p
        className="text-xs text-gray-500 mb-10 break-all font-mono"
        data-testid="not-found-path"
      >
        {location.pathname}
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={() => navigate(-1)}
          variant="outline"
          className="border-white/25 bg-white/5 text-white hover:bg-white/15 transition-colors"
          data-testid="not-found-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go back
        </Button>
        <Button
          onClick={() => navigate(inline ? '/dashboard/portal' : '/')}
          className="bg-amber-400 text-slate-900 hover:bg-amber-300 transition-colors"
          data-testid="not-found-home-btn"
        >
          <Home className="w-4 h-4 mr-2" />
          {inline ? 'Back to the portal' : 'Return home'}
        </Button>
      </div>
    </div>
  );

  if (inline) return <div className="max-w-4xl mx-auto">{body}</div>;

  return (
    <div
      className="min-h-screen flex items-center px-6 sm:px-16"
      style={{
        background:
          'linear-gradient(to bottom, rgba(10, 14, 26, 1) 0%, rgba(26, 35, 50, 1) 55%, rgba(45, 55, 72, 1) 100%)',
      }}
    >
      {body}
    </div>
  );
};

export default NotFoundPage;
